import moment from 'moment';
import { SubSink } from 'subsink';

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { AuthFacade, UserPermission } from '@mitsubishi/core';
import { ProjectSelectionsFacade } from '@mitsubishi/project/data-access';
import { CustomAccessory, Prices } from '@mitsubishi/shared/data-access';
import { BaseUiModule, CurrencyInputComponent } from '@mitsubishi/shared/ui';
import { UiDatepickerModule } from '@mitsubishi/ui/datepicker';

export type CustomAccessoryDialogData = {
  modelArticleCode: string;
  familySapCode: string;
  quantity?: number;
  accessory?: Partial<CustomAccessory>;
  forCustomUnit?: boolean;
  alternativeAccessoryPrices?: Prices;
  readonly?: boolean;
};

@Component({
  selector: 'apm-accessory-unit-dialog',
  templateUrl: './custom-accessory-dialog.component.html',
  styleUrls: ['./custom-accessory-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, BaseUiModule, UiDatepickerModule, MatCheckbox, CurrencyInputComponent],
})
export class CustomAccessoryDialogComponent implements OnInit, OnDestroy {
  #authFacade = inject(AuthFacade);
  #selectionFacade = inject(ProjectSelectionsFacade);

  dialogData = inject(MAT_DIALOG_DATA) as CustomAccessoryDialogData;
  dialogRef = inject(MatDialogRef<CustomAccessoryDialogComponent>);

  hasMenageCustomAndRfqPermission = this.#authFacade.hasPermission(UserPermission.ManageCustomAndRfq);

  currencySymbol = this.#selectionFacade.currencySymbol;

  get isCustomUnit() {
    return this.dialogData.forCustomUnit ?? false;
  }

  get quantity() {
    return this.dialogData.quantity ?? 1;
  }

  get accessoryPrices() {
    return this.dialogData.alternativeAccessoryPrices;
  }

  customAccessoryForm = new UntypedFormGroup({
    name: new FormControl(this.dialogData.accessory?.name ?? null, [Validators.required]),
    atpCode: new FormControl(this.dialogData.accessory?.atpCode ?? null),
    positionCode: new FormControl(
      this.dialogData.accessory?.positionCode ?? null,
      this.hasMenageCustomAndRfqPermission ? [Validators.pattern('^[0-9]*$')] : []
    ),
    commodityCode: new FormControl(
      { value: this.commodityCode, disabled: !this.hasMenageCustomAndRfqPermission || this.isCustomUnit },
      this.hasMenageCustomAndRfqPermission ? [Validators.required] : []
    ),
    materialCode: new FormControl(
      { value: this.materialCode, disabled: !this.hasMenageCustomAndRfqPermission || this.isCustomUnit },
      this.hasMenageCustomAndRfqPermission ? [Validators.required] : []
    ),
    priceList: new FormControl(
      this.getAccessoryPrice(),
      this.hasMenageCustomAndRfqPermission ? [Validators.required] : []
    ),
    sapOfferNumber: new FormControl(this.dialogData.accessory?.sapOfferNumber ?? null),
    validityDate: new FormControl(
      this.dialogData.accessory?.validityDate ? moment(this.dialogData.accessory?.validityDate) : this.validityDate,
      [Validators.required]
    ),
    sendToSap: new FormControl(this.dialogData.accessory?.sentToSap ?? true),
  });

  #subs: SubSink = new SubSink();

  ngOnInit() {
    this.#subs.sink = this.customAccessoryForm.controls['sapOfferNumber'].valueChanges.subscribe(value => {
      if (value) {
        this.customAccessoryForm.controls['positionCode'].addValidators([Validators.required]);
      } else {
        this.customAccessoryForm.controls['positionCode'].removeValidators([Validators.required]);
      }
      this.customAccessoryForm.controls['positionCode'].updateValueAndValidity();
    });

    if (this.dialogData.readonly) {
      this.customAccessoryForm.disable();
    }
  }

  get validityDate() {
    return moment().add(60, 'days');
  }

  get commodityCode() {
    return this.dialogData.accessory?.commodityCode ?? this.dialogData.familySapCode ?? null;
  }

  get materialCode() {
    return this.dialogData.accessory?.materialCode ?? this.dialogData.modelArticleCode ?? null;
  }

  create() {
    this.dialogRef.close(this.customAccessoryForm.getRawValue());
  }

  closeDialog() {
    this.dialogRef.close();
    this.#subs.unsubscribe();
  }

  private getAccessoryPrice() {
    const accessoryPrice = this.dialogData.readonly ? this.accessoryPrices?.customer : this.accessoryPrices?.priceList;

    return accessoryPrice ? accessoryPrice / this.quantity : null;
  }

  ngOnDestroy() {
    this.#subs.unsubscribe();
  }
}
